import { MenuItem } from "@components";
import {
  BackwardThin,
  BuildingsThin,
  ClockLight,
  FileInvoiceLight,
  PenFieldRegular,
  PuzzleLightFull,
  ToolboxLight,
  UserRegular,
} from "@icons";
import { useLocalstorage } from "@src/hooks/helperHooks";
import { LAST_APP_PAGE_VISITED_KEY } from "@src/modules/member/constants";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { INVOICE_LIST_PATH } from "../../../main/router/paths";
import {
  CUSTOM_FIELDS_LIST_PATH,
  INTEGRATIONS,
  INVOICE_SETTINGS_PATH,
  ORGANIZATION_PATH,
  PROFILE_PATH,
  SERVICES_LIST_PATH,
  TIME_ENTRIES_SETTINGS_PATH,
} from "../paths";

interface MenuItemComponentPropTypes {
  minimized: boolean;
  setMobileMenuOpen: (open: boolean) => void;
}

const memberSettingsMenuList = [
  {
    key: "back",
    component: (props: MenuItemComponentPropTypes) => {
      const { t } = useTranslation();
      const navigate = useNavigate();
      const [lastAppPageVisited] = useLocalstorage(
        LAST_APP_PAGE_VISITED_KEY,
        INVOICE_LIST_PATH(),
      );

      return (
        <button
          className={classNames("flex gap-2 items-center px-2 py-2")}
          onClick={() => navigate(lastAppPageVisited as string)}
        >
          <span className={classNames({ "mr-2": !props.minimized })}>
            <BackwardThin />
          </span>
          {!props.minimized && <span>{t("member.settings.back")}</span>}
        </button>
      );
    },
  },
  {
    key: "divider-1",
    component: () => {
      return <div className="w-full h-[1px] bg-gray-200 my-2" />;
    },
  },
  {
    key: "profile-settings",
    component: (props: MenuItemComponentPropTypes) => {
      const { t } = useTranslation();

      return (
        <MenuItem
          to={PROFILE_PATH()}
          icon={
            <div
              className={classNames(
                "md:text-gray-500",
                { "text-gray-500": props.minimized },
                { "text-white": !props.minimized },
              )}
            >
              <UserRegular width={13} />
            </div>
          }
          label={t("member.settings.profile.profile")}
          minimized={props.minimized}
          setMobileMenuOpen={props.setMobileMenuOpen}
        />
      );
    },
  },
  {
    key: "organization-settings",
    component: (props: MenuItemComponentPropTypes) => {
      const { t } = useTranslation();

      return (
        <MenuItem
          to={ORGANIZATION_PATH()}
          icon={
            <div
              className={classNames(
                "md:text-gray-500",
                { "text-gray-500": props.minimized },
                { "text-white": !props.minimized },
              )}
            >
              <BuildingsThin width={14} />
            </div>
          }
          label={t("member.settings.organization.organization")}
          minimized={props.minimized}
          setMobileMenuOpen={props.setMobileMenuOpen}
        />
      );
    },
  },
  {
    key: "divider-2",
    component: () => {
      return <div className="w-full h-[1px] bg-gray-200 my-2" />;
    },
  },
  {
    key: "invoice-settings",
    component: (props: MenuItemComponentPropTypes) => {
      const { t } = useTranslation();

      return (
        <MenuItem
          to={INVOICE_SETTINGS_PATH()}
          icon={
            <div
              className={classNames(
                "md:text-gray-500",
                { "text-gray-500": props.minimized },
                { "text-white": !props.minimized },
              )}
            >
              <FileInvoiceLight width={13} />
            </div>
          }
          label={t("member.settings.invoices.invoice")}
          minimized={props.minimized}
          setMobileMenuOpen={props.setMobileMenuOpen}
        />
      );
    },
  },
  {
    key: "services-settings",
    component: (props: MenuItemComponentPropTypes) => {
      const { t } = useTranslation();

      return (
        <MenuItem
          to={SERVICES_LIST_PATH()}
          icon={
            <div
              className={classNames(
                "md:text-gray-500",
                { "text-gray-500": props.minimized },
                { "text-white": !props.minimized },
              )}
            >
              <ToolboxLight width={14} />
            </div>
          }
          label={t("member.settings.services.services")}
          minimized={props.minimized}
          setMobileMenuOpen={props.setMobileMenuOpen}
        />
      );
    },
  },
  {
    key: "time-entries-settings",
    component: (props: MenuItemComponentPropTypes) => {
      const { t } = useTranslation();

      return (
        <MenuItem
          to={TIME_ENTRIES_SETTINGS_PATH()}
          icon={
            <div
              className={classNames(
                "md:text-gray-500",
                { "text-gray-500": props.minimized },
                { "text-white": !props.minimized },
              )}
            >
              <ClockLight width={14} />
            </div>
          }
          label={t("member.settings.timeEntries.timeEntries")}
          minimized={props.minimized}
          setMobileMenuOpen={props.setMobileMenuOpen}
        />
      );
    },
  },
  {
    key: "divider-3",
    component: () => {
      return <div className="w-full h-[1px] bg-gray-200 my-2" />;
    },
  },
  {
    key: "custom-fields",
    component: (props: MenuItemComponentPropTypes) => {
      const { t } = useTranslation();

      return (
        <MenuItem
          to={CUSTOM_FIELDS_LIST_PATH()}
          icon={
            <div
              className={classNames(
                "md:text-gray-500",
                { "text-gray-500": props.minimized },
                { "text-white": !props.minimized },
              )}
            >
              <PenFieldRegular />
            </div>
          }
          label={t("member.settings.customFields.customFields")}
          minimized={props.minimized}
          setMobileMenuOpen={props.setMobileMenuOpen}
        />
      );
    },
  },
  {
    key: "divider-4",
    component: () => {
      return <div className="w-full h-[1px] bg-gray-200 my-2" />;
    },
  },
  {
    key: "integrations",
    component: (props: MenuItemComponentPropTypes) => {
      const { t } = useTranslation();

      return (
        <MenuItem
          to={INTEGRATIONS()}
          icon={
            <div
              className={classNames(
                "md:text-gray-500",
                { "text-gray-500": props.minimized },
                { "text-white": !props.minimized },
              )}
            >
              <PuzzleLightFull width={20} />
            </div>
          }
          label={t("member.settings.integrations.integrations")}
          minimized={props.minimized}
          setMobileMenuOpen={props.setMobileMenuOpen}
        />
      );
    },
  },
];

export default memberSettingsMenuList;
